<template>
  <v-card
    rel="noopener"
    target="_blank"
    rounded="lg"
    elevation="0"
    border
    :title="getFeedNameFromFeedType(props.feed)"
  >
    <v-card-text class="pa-4">
      <div v-if="accountSubtitle" class="text-body-2 text-medium-emphasis mb-2">
        {{ accountSubtitle }}
      </div>
      <div class="d-flex flex-wrap align-center gap-2">
        <v-skeleton-loader
          v-if="props.loading === true"
          :loading="loading"
          type="list-item"
          width="100px"
        />
        <template v-else>
          <v-chip
            v-if="
              props.account &&
              isValid(props.account) &&
              props.account.status === IApp.IAccountStatus.Active
            "
            size="small"
            color="success"
            variant="tonal"
          >
            Active
          </v-chip>
          <v-chip
            v-else-if="
              props.account &&
              isValid(props.account) &&
              props.account.status === IApp.IAccountStatus.Paused
            "
            size="small"
            color="warning"
            variant="tonal"
          >
            Paused
          </v-chip>
          <v-chip
            v-else-if="
              props.account &&
              isValid(props.account) &&
              props.account.status === IApp.IAccountStatus.Archived
            "
            size="small"
            color="secondary"
            variant="tonal"
          >
            Archived
          </v-chip>
          <v-chip v-else size="small" variant="tonal">Not connected</v-chip>
          <v-chip
            v-if="props.account && isValid(props.account)"
            size="small"
            :color="isSynced(props.account) ? 'success' : 'error'"
            variant="tonal"
            :disabled="!canSyncOrEdit"
            @click="handleClickSync"
          >
            {{ isSynced(props.account) ? "Synced" : "Not synced" }}
          </v-chip>
        </template>
      </div>
    </v-card-text>
    <template v-slot:prepend>
      <div class="source-card__logo-wrap shrink-0">
        <ServiceLogo size="60px" :service="props.feed" />
      </div>
    </template>
    <template v-slot:append>
      <v-btn
        v-if="props.account && isValid(props.account)"
        variant="text"
        class="text-transform-none shrink-0"
        prepend-icon="mdi-sync"
        :disabled="!canSyncOrEdit"
        :loading="state.reloadLoading"
        @click="openSyncConfirmDialog"
      >
        Sync
      </v-btn>
    </template>
    <template v-slot:actions>
      <v-skeleton-loader
        v-if="props.loading === true"
        :loading="loading"
        type="list-item"
        width="120px"
        class="ms-auto"
      />
      <div
        v-if="props.account && props.loading !== true"
        class="source-card__action-buttons d-flex flex-column align-end ms-auto"
      >
        <div class="d-flex flex-wrap justify-end source-card__action-row">
          <v-btn
            color="primary"
            variant="tonal"
            prepend-icon="mdi-sync"
            class="text-transform-none"
            :disabled="!canSyncOrEdit"
            @click="goViewSyncStatus"
          >
            View Sync Status
          </v-btn>
          <v-btn
            color="primary"
            variant="outlined"
            prepend-icon="mdi-pencil"
            class="text-transform-none"
            :disabled="!canSyncOrEdit"
            @click="goEditSource"
          >
            Edit
          </v-btn>
        </div>
        <div class="d-flex flex-wrap justify-end source-card__action-row mt-2">
          <v-tooltip
            v-if="
              props.account?.status !== IApp.IAccountStatus.Paused &&
              props.account?.status !== IApp.IAccountStatus.Archived
            "
            text="Pause keeps this data source connected but stops automated sync/fetch jobs."
            location="top"
          >
            <template #activator="{ props: tooltipProps }">
              <v-btn
                v-bind="tooltipProps"
                color="secondary"
                variant="outlined"
                prepend-icon="mdi-pause-circle-outline"
                class="text-transform-none"
                :loading="state.pauseLoading"
                @click="openPauseConfirmDialog"
              >
                Pause
              </v-btn>
            </template>
          </v-tooltip>
          <v-tooltip
            v-else-if="props.account?.status === IApp.IAccountStatus.Paused"
            text="Resume re-enables this source for active sync/fetch jobs."
            location="top"
          >
            <template #activator="{ props: tooltipProps }">
              <v-btn
                v-bind="tooltipProps"
                color="success"
                variant="outlined"
                prepend-icon="mdi-play-circle-outline"
                class="text-transform-none"
                :loading="state.resumeLoading"
                @click="openResumeConfirmDialog"
              >
                Resume
              </v-btn>
            </template>
          </v-tooltip>
          <v-tooltip
            v-else-if="props.account?.status === IApp.IAccountStatus.Archived"
            text="Activate restores this archived source back to active usage."
            location="top"
          >
            <template #activator="{ props: tooltipProps }">
              <v-btn
                v-bind="tooltipProps"
                color="success"
                variant="outlined"
                prepend-icon="mdi-play-circle-outline"
                class="text-transform-none"
                :loading="state.activateLoading"
                @click="openActivateConfirmDialog"
              >
                Activate
              </v-btn>
            </template>
          </v-tooltip>
          <v-tooltip
            v-if="props.account?.status !== IApp.IAccountStatus.Archived"
            text="Archive keeps this data source but disables active usage until re-enabled."
            location="top"
          >
            <template #activator="{ props: tooltipProps }">
              <v-btn
                v-bind="tooltipProps"
                color="warning"
                variant="outlined"
                prepend-icon="mdi-archive-outline"
                class="text-transform-none"
                :loading="state.archiveLoading"
                @click="openArchiveConfirmDialog"
              >
                Archive
              </v-btn>
            </template>
          </v-tooltip>
          <v-tooltip
            text="Delete soft-deletes this source. It disappears from UI and stops future fetch/sync."
            location="top"
          >
            <template #activator="{ props: tooltipProps }">
              <v-btn
                v-bind="tooltipProps"
                color="error"
                variant="outlined"
                prepend-icon="mdi-delete-outline"
                class="text-transform-none"
                :loading="state.deleteLoading"
                @click="openDeleteConfirmDialog"
              >
                Delete
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </div>
      <v-btn
        v-if="!props.account && props.loading !== true"
        color="primary"
        variant="tonal"
        prepend-icon="mdi-link-variant"
        class="text-transform-none ms-auto"
        @click="handleClickConnect"
      >
        Connect
      </v-btn>
    </template>
  </v-card>

  <v-dialog v-model="state.syncConfirmDialogOpen" max-width="420" scrollable>
    <v-card rounded="lg" elevation="8">
      <v-card-title class="text-h6 font-weight-bold">Sync data?</v-card-title>
      <v-card-text class="text-body-2 text-medium-emphasis">
        This will start a sync for this data source. Do you want to continue?
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn
          variant="text"
          class="text-transform-none"
          :disabled="state.reloadLoading"
          @click="state.syncConfirmDialogOpen = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          class="text-transform-none"
          :loading="state.reloadLoading"
          @click="confirmSync"
        >
          Sync
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="state.deleteConfirmDialogOpen" max-width="420" scrollable>
    <v-card rounded="lg" elevation="8">
      <v-card-title class="text-h6 font-weight-bold">Delete data source?</v-card-title>
      <v-card-text class="text-body-2 text-medium-emphasis">
        This will soft delete this source. It will no longer appear in your list and data sync/fetch
        for this source will stop.
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn
          variant="text"
          class="text-transform-none"
          :disabled="state.deleteLoading"
          @click="state.deleteConfirmDialogOpen = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="error"
          variant="flat"
          class="text-transform-none"
          :loading="state.deleteLoading"
          @click="confirmDelete"
        >
          Delete
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="state.archiveConfirmDialogOpen" max-width="420" scrollable>
    <v-card rounded="lg" elevation="8">
      <v-card-title class="text-h6 font-weight-bold">Archive data source?</v-card-title>
      <v-card-text class="text-body-2 text-medium-emphasis">
        This will archive this source. It will be hidden from active cards and future sync/fetch
        runs.
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn
          variant="text"
          class="text-transform-none"
          :disabled="state.archiveLoading"
          @click="state.archiveConfirmDialogOpen = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="warning"
          variant="flat"
          class="text-transform-none"
          :loading="state.archiveLoading"
          @click="confirmArchive"
        >
          Archive
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="state.pauseConfirmDialogOpen" max-width="420" scrollable>
    <v-card rounded="lg" elevation="8">
      <v-card-title class="text-h6 font-weight-bold">Pause data source?</v-card-title>
      <v-card-text class="text-body-2 text-medium-emphasis">
        This keeps the source connected, but automated sync/fetch jobs will skip it while paused.
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn
          variant="text"
          class="text-transform-none"
          :disabled="state.pauseLoading"
          @click="state.pauseConfirmDialogOpen = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="secondary"
          variant="flat"
          class="text-transform-none"
          :loading="state.pauseLoading"
          @click="confirmPause"
        >
          Pause
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="state.resumeConfirmDialogOpen" max-width="420" scrollable>
    <v-card rounded="lg" elevation="8">
      <v-card-title class="text-h6 font-weight-bold">Resume data source?</v-card-title>
      <v-card-text class="text-body-2 text-medium-emphasis">
        This will set the source back to active and allow sync/fetch jobs again.
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn
          variant="text"
          class="text-transform-none"
          :disabled="state.resumeLoading"
          @click="state.resumeConfirmDialogOpen = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="success"
          variant="flat"
          class="text-transform-none"
          :loading="state.resumeLoading"
          @click="confirmResume"
        >
          Resume
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="state.activateConfirmDialogOpen" max-width="420" scrollable>
    <v-card rounded="lg" elevation="8">
      <v-card-title class="text-h6 font-weight-bold">Activate data source?</v-card-title>
      <v-card-text class="text-body-2 text-medium-emphasis">
        This will move the source from archived to active and allow usage/sync again.
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn
          variant="text"
          class="text-transform-none"
          :disabled="state.activateLoading"
          @click="state.activateConfirmDialogOpen = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="success"
          variant="flat"
          class="text-transform-none"
          :loading="state.activateLoading"
          @click="confirmActivate"
        >
          Activate
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { IApp } from "@/types";
import ServiceLogo from "./ServiceLogo.vue";
import { getFeedNameFromFeedType, isSynced, isValid, makeAlert } from "@/utils";
import { URLs } from "@/constants";
import { useAccountStore } from "@/stores/account";
import _ from "lodash";
import useRouteParams from "@/hooks/useRouteParams";
import { computed, reactive } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();

const props = defineProps<{
  userId: string;
  account?: IApp.IGoogleSearchConsoleAccount | IApp.IGoogleAnalyticsAccount | IApp.IBirdEyeAccount;
  feed: IApp.IFeedTypes;
  loading?: boolean;
  /** When set, hides redundant account name if it matches the customer name. */
  customerName?: string;
}>();

/** Linked account label; omitted when identical to customer name to reduce repetition. */
const accountSubtitle = computed(() => {
  const name = props.account?.name?.trim();
  if (!name) return "";
  const cn = props.customerName?.trim();
  if (cn && name.toLowerCase() === cn.toLowerCase()) return "";
  return name;
});

const state = reactive({
  reloadLoading: false,
  syncConfirmDialogOpen: false,
  pauseConfirmDialogOpen: false,
  pauseLoading: false,
  resumeConfirmDialogOpen: false,
  resumeLoading: false,
  activateConfirmDialogOpen: false,
  activateLoading: false,
  archiveConfirmDialogOpen: false,
  archiveLoading: false,
  deleteConfirmDialogOpen: false,
  deleteLoading: false
});

const accountStore = useAccountStore();

const { customerId } = useRouteParams();

const canSyncOrEdit = computed(() => {
  const acc = props.account;
  if (!acc || !isValid(acc)) return false;
  return acc.status !== IApp.IAccountStatus.Paused && acc.status !== IApp.IAccountStatus.Archived;
});

const goViewSyncStatus = () => {
  if (!canSyncOrEdit.value || !props.account) return;
  router.push(URLs.PAGE_SYNC_MONITOR(customerId.value, props.account.account));
};

const goEditSource = () => {
  if (!canSyncOrEdit.value || !props.account) return;
  router.push(URLs.PAGE_SOURCE_EDIT(customerId.value, props.account.account));
};

const handleClickSync = () => {
  if (!canSyncOrEdit.value || !props.account) return;
  router.push(URLs.PAGE_SYNC_MONITOR(customerId.value, props.account.account));
};

const handleClickConnect = async () => {
  if (_.includes([IApp.IFeedTypes.GoogleSearchConsole], props.account?.feed)) {
    const response = await accountStore.createOAuthURL(
      props.userId,
      customerId?.value!,
      props.account?.feed!
    );
    if (response.status) {
      window.open(response.data.url, "_blank")?.focus();
    } else {
      makeAlert(response.message, "error");
    }
  } else {
    makeAlert("Coming soon!");
  }
};
const openSyncConfirmDialog = () => {
  if (!canSyncOrEdit.value) return;
  state.syncConfirmDialogOpen = true;
};

const confirmSync = async () => {
  if (!props.account || !canSyncOrEdit.value) {
    state.syncConfirmDialogOpen = false;
    return;
  }
  state.reloadLoading = true;
  try {
    const response = await accountStore.reloadData(
      props.userId,
      customerId?.value!,
      props.account.account
    );
    if (response.status) {
      state.syncConfirmDialogOpen = false;
      makeAlert("Sync initiated successfully", "success");
    } else {
      makeAlert(response.message, "error");
    }
  } finally {
    state.reloadLoading = false;
  }
};

const openDeleteConfirmDialog = () => {
  state.deleteConfirmDialogOpen = true;
};

const openArchiveConfirmDialog = () => {
  state.archiveConfirmDialogOpen = true;
};

const openPauseConfirmDialog = () => {
  state.pauseConfirmDialogOpen = true;
};

const openResumeConfirmDialog = () => {
  state.resumeConfirmDialogOpen = true;
};

const openActivateConfirmDialog = () => {
  state.activateConfirmDialogOpen = true;
};

const confirmPause = async () => {
  if (!props.account || !customerId.value) {
    state.pauseConfirmDialogOpen = false;
    return;
  }
  state.pauseLoading = true;
  try {
    const response = await accountStore.pauseAccount(
      props.userId,
      customerId.value,
      props.account.account
    );
    if (response.status) {
      makeAlert("Data source paused successfully", "success");
      state.pauseConfirmDialogOpen = false;
    } else {
      makeAlert(response.message, "error");
    }
  } finally {
    state.pauseLoading = false;
  }
};

const confirmResume = async () => {
  if (!props.account || !customerId.value) {
    state.resumeConfirmDialogOpen = false;
    return;
  }
  state.resumeLoading = true;
  try {
    const response = await accountStore.resumeAccount(
      props.userId,
      customerId.value,
      props.account.account
    );
    if (response.status) {
      makeAlert("Data source resumed successfully", "success");
      state.resumeConfirmDialogOpen = false;
    } else {
      makeAlert(response.message, "error");
    }
  } finally {
    state.resumeLoading = false;
  }
};

const confirmActivate = async () => {
  if (!props.account || !customerId.value) {
    state.activateConfirmDialogOpen = false;
    return;
  }
  state.activateLoading = true;
  try {
    const response = await accountStore.activateAccount(
      props.userId,
      customerId.value,
      props.account.account
    );
    if (response.status) {
      makeAlert("Data source activated successfully", "success");
      state.activateConfirmDialogOpen = false;
    } else {
      makeAlert(response.message, "error");
    }
  } finally {
    state.activateLoading = false;
  }
};

const confirmArchive = async () => {
  if (!props.account || !customerId.value) {
    state.archiveConfirmDialogOpen = false;
    return;
  }
  state.archiveLoading = true;
  try {
    const response = await accountStore.archiveAccount(
      props.userId,
      customerId.value,
      props.account.account
    );
    if (response.status) {
      makeAlert("Data source archived successfully", "success");
      state.archiveConfirmDialogOpen = false;
    } else {
      makeAlert(response.message, "error");
    }
  } finally {
    state.archiveLoading = false;
  }
};

const confirmDelete = async () => {
  if (!props.account || !customerId.value) {
    state.deleteConfirmDialogOpen = false;
    return;
  }
  state.deleteLoading = true;
  try {
    const response = await accountStore.softDeleteAccount(
      props.userId,
      customerId.value,
      props.account.account
    );
    if (response.status) {
      makeAlert("Data source deleted successfully", "success");
      state.deleteConfirmDialogOpen = false;
    } else {
      makeAlert(response.message, "error");
    }
  } finally {
    state.deleteLoading = false;
  }
};
</script>

<style scoped>
.source-card__logo-wrap {
  margin-inline-end: var(--thor-profile-avatar-name-gap);
}

:deep(.v-card-actions) {
  padding: 8px var(--thor-profile-card-padding) var(--thor-profile-card-padding);
}

.source-card__action-buttons {
  row-gap: 0;
}

.source-card__action-row {
  column-gap: 10px;
  row-gap: 8px;
}
</style>
